import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Edit2, Save, X, Gift, Loader2, Tag, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEHOLDER_IMG = 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=200';

const AdminHamperPricing = ({ adminToken }) => {
  const [hampers, setHampers] = useState([]);
  const [itemPrices, setItemPrices] = useState([]);
  const [loadingHampers, setLoadingHampers] = useState(true);
  const [loadingItems, setLoadingItems] = useState(true);
  const [activeTab, setActiveTab] = useState('hampers'); // 'hampers' | 'items'

  const [editing, setEditing] = useState(null);
  const [editingBase, setEditingBase] = useState(null);
  const [editingItem, setEditingItem] = useState(null); // { option_id, value }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHampers();
    fetchItemPrices();
  }, []);

  const fetchHampers = async () => {
    try {
      const res = await axios.get(`${API}/gift-hampers`);
      setHampers(res.data);
    } catch {
      toast.error('Failed to load hamper config');
    } finally {
      setLoadingHampers(false);
    }
  };

  const fetchItemPrices = async () => {
    try {
      const res = await axios.get(`${API}/admin/hamper-item-prices`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setItemPrices(res.data);
    } catch {
      toast.error('Failed to load item prices');
    } finally {
      setLoadingItems(false);
    }
  };

  const saveOptionPrice = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/gift-hampers/${editing.hamper_id}/option-price`,
        { group_id: editing.group_id, option_id: editing.option_id, addon_price: parseFloat(editing.value) || 0 },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Price updated');
      setEditing(null);
      fetchHampers();
    } catch {
      toast.error('Failed to update price');
    } finally {
      setSaving(false);
    }
  };

  const saveBasePrice = async () => {
    if (!editingBase) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/gift-hampers/${editingBase.hamper_id}/base-price`,
        { base_price: parseFloat(editingBase.value) || 0 },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Base price updated');
      setEditingBase(null);
      fetchHampers();
    } catch {
      toast.error('Failed to update base price');
    } finally {
      setSaving(false);
    }
  };

  const saveItemPrice = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/hamper-item-prices/${editingItem.option_id}`,
        { display_price: parseFloat(editingItem.value) || 0 },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Item price updated');
      setEditingItem(null);
      fetchItemPrices();
    } catch {
      toast.error('Failed to update item price');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-hamper-pricing">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-bold text-accent">Gift Hamper Pricing</h2>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('hampers')}
          data-testid="tab-hamper-base"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            activeTab === 'hampers' ? 'bg-primary text-white shadow' : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
          }`}
        >
          <Package className="h-4 w-4" /> Hamper Prices
        </button>
        <button
          onClick={() => setActiveTab('items')}
          data-testid="tab-item-prices"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            activeTab === 'items' ? 'bg-primary text-white shadow' : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
          }`}
        >
          <Tag className="h-4 w-4" /> Item Prices
          <Badge variant="outline" className="ml-1 text-xs h-5">{itemPrices.length}</Badge>
        </button>
      </div>

      {/* ── TAB: Hamper base prices ── */}
      {activeTab === 'hampers' && (
        <>
          <p className="text-sm text-muted-foreground">
            Edit base prices for each Gift Hamper. Changes apply immediately.
          </p>
          {loadingHampers ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4">
              {hampers.map((hamper) => (
                <Card key={hamper.id} className="p-5 border-amber-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-accent text-lg">{hamper.name}</h3>
                      <p className="text-sm text-muted-foreground">{hamper.tagline}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Base:</span>
                      {editingBase?.hamper_id === hamper.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium">₹</span>
                          <Input
                            type="number"
                            value={editingBase.value}
                            onChange={e => setEditingBase(prev => ({ ...prev, value: e.target.value }))}
                            className="w-20 h-7 text-sm"
                            min={0}
                            data-testid={`base-price-input-${hamper.id}`}
                          />
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={saveBasePrice} disabled={saving}>
                            <Save className="h-3.5 w-3.5 text-green-600" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingBase(null)}>
                            <X className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-primary">₹{hamper.base_price}</span>
                          <Button
                            size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => setEditingBase({ hamper_id: hamper.id, value: String(hamper.base_price) })}
                            data-testid={`edit-base-price-${hamper.id}`}
                          >
                            <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {hamper.selection_groups.map((group) => (
                      <div key={group.id}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          {group.label}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {group.options.map((option) => {
                            const isEditing = editing?.hamper_id === hamper.id && editing?.group_id === group.id && editing?.option_id === option.id;
                            return (
                              <div key={option.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                                <span className="text-sm text-accent">{option.name}</span>
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground">+₹</span>
                                    <Input
                                      type="number"
                                      value={editing.value}
                                      onChange={e => setEditing(prev => ({ ...prev, value: e.target.value }))}
                                      className="w-16 h-6 text-xs"
                                      min={0}
                                      data-testid={`option-price-input-${option.id}`}
                                    />
                                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveOptionPrice} disabled={saving}>
                                      <Save className="h-3 w-3 text-green-600" />
                                    </Button>
                                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditing(null)}>
                                      <X className="h-3 w-3 text-destructive" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span className={`text-xs font-medium ${option.addon_price > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                                      {option.addon_price > 0 ? `+₹${option.addon_price}` : 'No extra'}
                                    </span>
                                    <Button
                                      size="icon" variant="ghost" className="h-6 w-6"
                                      onClick={() => setEditing({ hamper_id: hamper.id, group_id: group.id, option_id: option.id, value: String(option.addon_price) })}
                                      data-testid={`edit-option-${option.id}`}
                                    >
                                      <Edit2 className="h-3 w-3 text-muted-foreground" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── TAB: Item display prices ── */}
      {activeTab === 'items' && (
        <>
          <p className="text-sm text-muted-foreground">
            Set the <strong>display price</strong> shown to customers when picking items inside a hamper.
            The total is: <em>Hamper base price + sum of selected item prices</em>.
          </p>
          {loadingItems ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="item-prices-grid">
              {itemPrices.map((item) => (
                <div
                  key={item.option_id}
                  className="flex items-center gap-3 bg-white border border-amber-100 rounded-xl p-3 shadow-sm"
                  data-testid={`item-price-row-${item.option_id}`}
                >
                  {/* Thumbnail */}
                  <div className="h-12 w-12 rounded-lg overflow-hidden flex-shrink-0 bg-amber-50">
                    <img
                      src={item.image || PLACEHOLDER_IMG}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={e => { e.target.src = PLACEHOLDER_IMG; }}
                    />
                  </div>
                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-accent truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.option_id}</p>
                  </div>
                  {/* Price editor */}
                  {editingItem?.option_id === item.option_id ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        value={editingItem.value}
                        onChange={e => setEditingItem(prev => ({ ...prev, value: e.target.value }))}
                        className="w-16 h-7 text-sm"
                        min={0}
                        data-testid={`item-price-input-${item.option_id}`}
                        autoFocus
                      />
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={saveItemPrice} disabled={saving}>
                        <Save className="h-3.5 w-3.5 text-green-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingItem(null)}>
                        <X className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`text-sm font-bold ${item.display_price > 0 ? 'text-primary' : 'text-gray-400'}`}>
                        {item.display_price > 0 ? `₹${item.display_price}` : '₹0'}
                      </span>
                      <Button
                        size="icon" variant="ghost" className="h-7 w-7"
                        onClick={() => setEditingItem({ option_id: item.option_id, value: String(item.display_price) })}
                        data-testid={`edit-item-price-${item.option_id}`}
                      >
                        <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {itemPrices.length === 0 && (
                <div className="col-span-2 text-center text-muted-foreground py-10">
                  No item options found. Restart the backend to seed them.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminHamperPricing;
